/* eslint-disable react-hooks/rules-of-hooks */
import PropTypes from "prop-types";
import { useState, useEffect } from "react";
import { useSnackbar } from "components/GlobalSnackbar";
import { handleApiError } from "utils/errorHandler";

// @mui material components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import Checkbox from "@mui/material/Checkbox";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";

// API
import { apiGetAllCategories, apiSearchCategories } from "api/categories";

export default function data({
  onRowSelect,
  selectedRow,
  currentPage,
  itemsPerPage,
  search,
  refreshTrigger,
  editingCell = null,
  editValue = '',
  onCellEdit = () => {},
  onSaveEdit = () => {},
  onCancelEdit = () => {},
  onEditValueChange = () => {},
  onEditKeyDown = () => {},
  saving = false,
}) {
  const [categoryData, setCategoryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const { showError } = useSnackbar();

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading(true);
        let response;
        // Always refresh: if all search fields are empty, show all data (matches Penyewa/Pelanggan behavior)
        if (
          search &&
          (typeof search.kategori_kutipan === "string" || typeof search.kod_kategori === "string")
        ) {
          const trimmedKategori = (search.kategori_kutipan || "").trim();
          const trimmedKod = (search.kod_kategori || "").trim();
          if (trimmedKategori !== "" || trimmedKod !== "") {
            const params = {};
            if (trimmedKategori !== "") params.kategori_kutipan = trimmedKategori;
            if (trimmedKod !== "") params.kod_kategori = trimmedKod;
            response = await apiSearchCategories(params, currentPage, itemsPerPage);
          } else {
            response = await apiGetAllCategories(currentPage, itemsPerPage);
          }
        } else {
          response = await apiGetAllCategories(currentPage, itemsPerPage);
        }
        if (response.data) {
          // Filter out RESERVE from the category list
          const filteredData = response.data.filter(cat => cat.kategori_kutipan !== 'RESERVE');
          setCategoryData(filteredData);
          setTotalPages(response.pagination.totalPages);
          setTotalItems(response.pagination.totalItems);
        } else {
          // Filter out RESERVE when response is array directly
          const filteredData = Array.isArray(response) ? response.filter(cat => cat.kategori_kutipan !== 'RESERVE') : response;
          setCategoryData(filteredData);
        }
      } catch (error) {
        handleApiError(error, showError);
        setCategoryData([]);
      } finally {
        setLoading(false);
      }
    };
    fetchCategories();
  }, [currentPage, itemsPerPage, refreshTrigger]);

  const CategoryName = ({ name }) => (
    <MDBox display="flex" alignItems="center" lineHeight={1}>
      <MDTypography variant="button" fontWeight="medium" ml={0.05} lineHeight={1}>
        {name}
      </MDTypography>
    </MDBox>
  );

  CategoryName.propTypes = {
    name: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  };

  const CategoryCode = ({ code }) => (
    <MDBox display="flex" alignItems="center" lineHeight={1}>
      <MDTypography variant="caption" color="text" fontWeight="medium" ml={0.05} lineHeight={1}>
        {code}
      </MDTypography>
    </MDBox>
  );

  CategoryCode.propTypes = {
    code: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  };

  const generateRows = () => {
    if (loading) {
      return [
        {
          select: (
            <input type="checkbox" checked={false} disabled style={{ cursor: "not-allowed" }} />
          ),
          kategori_kutipan: <CategoryName name="Loading..." />,
          kod_kategori: <CategoryCode code="..." />,
        },
      ];
    }

    // No need to filter on frontend, backend search already applied
    return categoryData.map((category) => {
      const isChecked = !!(selectedRow && selectedRow.id === category.id);
      return {
        select: (
          <input
            type="checkbox"
            checked={isChecked}
            onChange={() => {
              if (isChecked) {
                onRowSelect && onRowSelect(null); // Uncheck
              } else {
                onRowSelect && onRowSelect(category); // Check
              }
            }}
            style={{ cursor: "pointer" }}
          />
        ),
        kategori_kutipan: editingCell?.rowId === category.id && editingCell?.column === 'kategori_kutipan' ? (
          <MDBox display="flex" alignItems="center" gap={0.5}>
            <TextField
              value={editValue}
              onChange={(e) => onEditValueChange(e.target.value)}
              onKeyDown={onEditKeyDown}
              size="small"
              autoFocus
              disabled={saving}
              fullWidth
              sx={{
                '& .MuiInputBase-input': {
                  fontSize: '0.875rem',
                  py: 0.25,
                  px: 0.75,
                }
              }}
            />
            <IconButton size="small" onClick={onSaveEdit} disabled={saving} color="success" sx={{ p: 0.5 }}>
              <CheckIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={onCancelEdit} disabled={saving} color="error" sx={{ p: 0.5 }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </MDBox>
        ) : (
          <MDBox
            display="flex"
            alignItems="center"
            gap={1}
            sx={{
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                borderRadius: 1,
              },
              px: 1,
              py: 0.25,
            }}
            onClick={() => onCellEdit(category.id, 'kategori_kutipan', category.kategori_kutipan, category)}
          >
            <MDTypography variant="button" fontWeight="medium">
              {category.kategori_kutipan || "Unknown"}
            </MDTypography>
            <EditIcon sx={{ fontSize: 14, color: 'action.disabled', opacity: 0.5 }} />
          </MDBox>
        ),
        kod_kategori: editingCell?.rowId === category.id && editingCell?.column === 'kod_kategori' ? (
          <MDBox display="flex" alignItems="center" gap={0.5}>
            <TextField
              value={editValue}
              onChange={(e) => onEditValueChange(e.target.value)}
              onKeyDown={onEditKeyDown}
              size="small"
              autoFocus
              disabled={saving}
              fullWidth
              sx={{
                '& .MuiInputBase-input': {
                  fontSize: '0.875rem',
                  py: 0.25,
                  px: 0.75,
                }
              }}
            />
            <IconButton size="small" onClick={onSaveEdit} disabled={saving} color="success" sx={{ p: 0.5 }}>
              <CheckIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={onCancelEdit} disabled={saving} color="error" sx={{ p: 0.5 }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </MDBox>
        ) : (
          <MDBox
            display="flex"
            alignItems="center"
            gap={1}
            sx={{
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                borderRadius: 1,
              },
              px: 1,
              py: 0.25,
            }}
            onClick={() => onCellEdit(category.id, 'kod_kategori', category.kod_kategori, category)}
          >
            <MDTypography variant="caption" color="text" fontWeight="medium">
              {category.kod_kategori || "-"}
            </MDTypography>
            <EditIcon sx={{ fontSize: 14, color: 'action.disabled', opacity: 0.5 }} />
          </MDBox>
        ),
      };
    });
  };

  return {
    columns: [
      { Header: "", accessor: "select", width: "10%", align: "center" },
      { Header: "kategori kutipan", accessor: "kategori_kutipan", width: "60%", align: "left" },
      { Header: "kod kategori", accessor: "kod_kategori", width: "30%", align: "left" },
    ],

    rows: generateRows(),
    pagination: {
      totalPages,
      totalItems,
      currentPage,
      actualItemsOnPage: categoryData.length, // Add actual items displayed on current page
    },
    rawData: categoryData, // Expose raw data for duplicate checking
  };
}
